import React, { useState, useEffect, memo } from "react";

// Components
import Tooltip from "@mui/material/Tooltip";
import TablePagination from "@mui/material/TablePagination";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";

// Styles - Usar los mismos estilos que TableModel
import {
  Cell,
  CustomLink,
  ContainerActions,
  HeaderCell,
  ContainerDiv,
  ContainerItem,
  StickyColumn,
} from "./styles";
import { 
  CustomTableContainer, 
  CustomTableHead, 
  CustomTable, 
  TableContent as CustomTableBody, 
  PaperContainer 
} from "../TableWorkflow/styles";

// Icons
import { PlayCircle, SyncRounded } from "@mui/icons-material";

// Utils
import { ColorsContext } from "../../../../Context/ColorsContext";
import { FormatDate } from "../../../../utils/formatDate";
import { useSortByColumn } from "../../../../Hooks/useSortByColumn";
import { useTranslation } from "react-i18next";
import { useOffline } from "../../../../Context/OfflineContext";
import { Box, Card, CardContent, Divider, Table, TableHead, Typography, Button, useMediaQuery, useTheme } from "@mui/material";
import { ContainerCell, CustomIconButton, InfoCell, SectionInfo } from "../TableWorkflow/styles";
import { getIcon } from "../../../../utils/iconConfig";
import { snakeToCamel } from "../../../../utils/commonFunctions/convert";
import { Link } from "react-router-dom";
import Router from "../../../../router";
import { ChevronRight } from "@mui/icons-material";

const MobileWorkflowCard = ({
  row,
  workflow,
  handleProcessForm,
  connectionStatus,
  isFormDownloadedForOffline,
  getEditedOfflineFormData,
  hasEditedData
}) => {
  const { t } = useTranslation();
  const { colors } = React.useContext(ColorsContext);
  const { format } = new FormatDate();

  return (
    <Card sx={{ boxShadow: 2, borderRadius: 2, mb: 1.5 }}>
      <CardContent sx={{ position: "relative", pb: "16px !important" }}>
        {/* Row 1: Code and Status */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="h6" sx={{ color: '#000000', fontWeight: 'bold' }}>
              {row.code !== null ? row.code : row.id}
            </Typography>
          </Box>

          <Box sx={{ 
            display: 'flex', 
            flexDirection: 'row', 
            alignItems: 'center', 
            textAlign: 'right', 
            justifyContent: 'flex-end',
            maxWidth: '60%', 
          }}>
            <Box sx={{ display: 'flex', transform: 'scale(0.8)', transformOrigin: 'center', flexShrink: 0 }}>
              {getIcon(row.workflowStatus)}
            </Box>
            <Box sx={{ display: 'flex' }}>
              <Typography sx={{ 
                fontSize: '0.875rem', 
                color: colors.grayFont, 
                lineHeight: 1.2, 
                wordBreak: 'break-word',
                textAlign: 'left'
              }}>
                {(row.workflowStatus?.id >= 0 && t(`workflowManagement.flowState.status.${snakeToCamel(row.workflowStatus?.type)}`)) || row.workflowStatus?.title || ""}
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* Row 2: Title */}
        {workflow?.hasTitle && row.title && (
          <Box sx={{ mb: 1.5 }}>
            <Typography sx={{ fontSize: '12px', color: '#6c6c6c' }}>
              {t("lotoWorklow.table.title")}
            </Typography>
            <Typography sx={{ fontSize: '12px', fontWeight: 'bold', color: '#3b3b3b' }}>
              {row.title}
            </Typography>
          </Box>
        )}

        {/* Row 3: Sitio y Contrato */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1.5 }}>
          <Box sx={{ flex: 1 }}>
            <Typography sx={{ fontSize: '12px', color: '#6c6c6c' }}>
              {t("lotoWorklow.table.site")}
            </Typography>
            <Typography sx={{ fontSize: '12px', fontWeight: 'bold', color: '#3b3b3b' }}>
              {row.plant?.name || '-'}
            </Typography>
          </Box>
          <Box sx={{ flex: 1, textAlign: 'right' }}>
            <Typography sx={{ fontSize: '12px', color: '#6c6c6c' }}>
              {t("lotoWorklow.table.contract")}
            </Typography>
            <Typography sx={{ fontSize: '12px', fontWeight: 'bold', color: '#3b3b3b' }}>
              {row.workflowData?.contract?.code || row.contract?.code || '-'}
            </Typography>
          </Box>
        </Box>

        {/* Row 4: Última actualización y Vigencia */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1.5 }}>
          <Box sx={{ flex: 1 }}>
            <Typography sx={{ fontSize: '12px', color: '#6c6c6c' }}>
              {t("lotoWorklow.table.lastUpdate")}
            </Typography>
            <Typography sx={{ fontSize: '12px', fontWeight: 'bold', color: '#3b3b3b' }}>
              {format({ date: row?.updatedAt, format: true }).toString()}
            </Typography>
          </Box>
          <Box sx={{ flex: 1, textAlign: 'right' }}>
            <Typography sx={{ fontSize: '12px', color: '#6c6c6c' }}>
              {t("lotoWorklow.table.instanceValidity")}
            </Typography>
            {workflow?.hasValidity && row?.validityFrom ? (
                <Typography sx={{ fontSize: '12px', fontWeight: 'bold', color: '#3b3b3b' }}>
                  {format({ date: row?.validityFrom, formatWithoutHour: true }).toString()}
                </Typography>
            ) : (
              <Typography sx={{ fontSize: '12px', fontWeight: 'bold', color: '#3b3b3b' }}>
                -
              </Typography>
            )}
          </Box>
        </Box>

        <Divider sx={{ mx: -2, my: 1.5 }} />

        {!!getEditedOfflineFormData(row.id) && (
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5, gap: 1, color: colors.grayDemo }}>
            <SyncRounded fontSize="small" />
            <Typography sx={{ fontSize: '12px', fontWeight: 'bold' }}>
              {t("lotoWorklow.table.notSynchronized")}
            </Typography>
          </Box>
        )}

        {/* Buttons Row */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Tooltip title={t("common.actions.process")}>
              <CustomIconButton
                size="small"
                style={{ backgroundColor: colors.buttonActionColor, color: '#fff' }}
                onClick={(e) => {
                  e.stopPropagation();
                  if (handleProcessForm) {
                    handleProcessForm(row);
                  }
                }}
                disabled={(!connectionStatus && !isFormDownloadedForOffline(row.id)) || hasEditedData}
              >
                <PlayCircle fontSize="small" />
              </CustomIconButton>
            </Tooltip>
          </Box>

          <Box>
            <Link
              to={Router.appOfflineWorkflowDetail
                .replace(":workflowId", row.workflowId)
                .replace(":id", row.workflowData?.id || row.id)}
              style={{ textDecoration: 'none' }}
              onClick={(e) => {
                if (!connectionStatus && !isFormDownloadedForOffline(row.id)) e.preventDefault();
              }}
            >
              <Button
                variant="contained"
                endIcon={<ChevronRight fontSize="small" />}
                disabled={!connectionStatus && !isFormDownloadedForOffline(row.id)}
                sx={{ 
                  textTransform: 'none', 
                  backgroundColor: '#eaf4fe',
                  color: '#325073', 
                  fontWeight: 500,
                  fontSize: '12px',
                  boxShadow: 'none',
                  borderRadius: '8px',
                  px: 2,
                  '&:hover': {
                    backgroundColor: '#dcecfc',
                    boxShadow: 'none',
                  }
                }}
              >
                {t("common.actions.viewMore")}
              </Button>
            </Link>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};

function EnhancedTableHead(props) {
  const { t } = useTranslation();
  const { headers } = props;

  return (
    <CustomTableHead>
      <TableRow>
        {headers.map((headCell) => (
          <HeaderCell
            key={headCell.id}
            align={headCell.center ? "center" : "left"}
            dataHead={headCell}
          >
            {t(headCell.label) || headCell.label}
          </HeaderCell>
        ))}
      </TableRow>
    </CustomTableHead>
  );
}

export default memo(function TableLoto({
  headers,
  filteredRequest,
  isList = false,
  defaultRowsPerPage = 25,
  defaultSort = null,
  handleProcessForm,
  onRowClick,
  loading
}) {
  const { format } = new FormatDate();
  const { t } = useTranslation();
  const { colors } = React.useContext(ColorsContext);
  const { isFormDownloadedForOffline, connectionStatus, getEditedOfflineFormData } = useOffline();

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(defaultRowsPerPage);
  const { order, orderBy, getComparator, stableSort } = useSortByColumn({ defaultSort });
  
  const theme = useTheme();
  const isTablet = useMediaQuery(theme.breakpoints.up("sm"));

  useEffect(() => {
    setPage(0);
  }, [filteredRequest]);

  const handleChangePage = (_, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Evitar un salto de layout cuando se llegue a la última página con filas vacías.
  const emptyRows =
    page > 0
      ? Math.max(0, (1 + page) * rowsPerPage - filteredRequest.length)
      : 0;

  return (
    <Box sx={{ width: "100%" }}>
      <PaperContainer
        sx={{
          backgroundColor: isTablet ? undefined : 'transparent',
          boxShadow: isTablet ? undefined : 'none'
        }}>
        {!loading && 
          <>
            {isTablet ? (
              <>
                <CustomTableContainer isCustom={true}>
                  <CustomTable stickyHeader
                    >
                      <EnhancedTableHead
                        headers={headers}
                      />

                      {filteredRequest.length > 0 ?
                        <CustomTableBody>
                          {stableSort(filteredRequest, getComparator(order, orderBy))
                            .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                            .map((row, index) => {
                              const labelId = `enhanced-table-checkbox-${index}`;

                              return (
                                <TableRow
                                  hover
                                  role="checkbox"
                                  tabIndex={-1}
                                  key={row.id}
                                  sx={{ cursor: "default" }}
                                >
                                  <StickyColumn direction="left" className="sticky">
                                    <ContainerDiv>
                                      <ContainerItem>
                                        <CustomLink
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            if (onRowClick) {
                                              onRowClick(row);
                                            }
                                          }}
                                          style={{
                                            color: !connectionStatus && !isFormDownloadedForOffline(row.id) 
                                              ? '#999'
                                              : colors?.detailTextColor ,
                                            cursor: !connectionStatus && !isFormDownloadedForOffline(row.id) 
                                              ? 'not-allowed' 
                                              : 'pointer'
                                          }}
                                        >
                                          {row.code}
                                        </CustomLink>
                                      </ContainerItem>
                                    </ContainerDiv>
                                  </StickyColumn>
                                  
                                  <Cell sx={{ minWidth: 150 }}>
                                    <ContainerCell>
                                      {getIcon(row.workflowStatus)}
                                      <SectionInfo>
                                        <InfoCell>
                                          <p style={{ fontSize: "0.875rem", color: colors.grayFont }}>
                                            {(row.workflowStatus?.id >= 0 &&
                                              t(
                                                `workflowManagement.flowState.status.${snakeToCamel(
                                                  row.workflowStatus?.type
                                                )}`
                                              )) ||
                                              ""}
                                          </p>
                                        </InfoCell>
                                        <InfoCell>
                                          {row.workflowStatus?.title || ""}
                                        </InfoCell>
                                      </SectionInfo>
                                    </ContainerCell>
                                  </Cell>
                                  <Cell sx={{ minWidth: 200 }}>
                                    {row.plant?.name}
                                  </Cell>
                                  <Cell sx={{ minWidth: 150 }}>
                                    {format({
                                      date: row.updatedAt,
                                      format: true,
                                    }).toString()}
                                  </Cell>
                                  <Cell sx={{ minWidth: 150 }} align="center">
                                    {!!getEditedOfflineFormData(row.id) && (
                                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, color: colors.grayDemo }}>
                                        <SyncRounded fontSize="small" />
                                        <span style={{ fontSize: '0.875rem', fontWeight: 500 }}>{t("lotoWorklow.table.notSynchronized")}</span>
                                      </Box>
                                    )}
                                  </Cell>
                                  <StickyColumn direction="right">
                                    <ContainerActions>
                                      <Tooltip title={t("common.actions.process")}>
                                        <CustomIconButton
                                          size="small"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            if (handleProcessForm) {
                                              handleProcessForm(row);
                                            }
                                          }}
                                          disabled={(!connectionStatus && !isFormDownloadedForOffline(row.id)) || !!getEditedOfflineFormData(row.id)}
                                        >
                                          <PlayCircle />
                                        </CustomIconButton>
                                      </Tooltip>
                                    </ContainerActions>
                                  </StickyColumn>
                                </TableRow>
                              );
                            })}
                        </CustomTableBody>
                        :
                        <CustomTableBody>
                          <TableRow>
                            <Cell colSpan={5} rowSpan={15} align="center" sx={{ paddingTop: "3rem" }}>
                              {t("lotoWorklow.table.noDownloadedForms")}
                            </Cell>
                          </TableRow>
                        </CustomTableBody>
                      }
                  </CustomTable>
                </CustomTableContainer>
                <TablePagination
                  rowsPerPageOptions={[5, 10, 25]}
                  component="div"
                  count={filteredRequest.length}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  onPageChange={handleChangePage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                  labelRowsPerPage={t("common.itemsPerPage")}
                  labelDisplayedRows={({ from, to, count }) => {
                    return "" + from + "-" + to + ` ${t("common.of")} ` + count;
                  }}
                />
              </>
            ) : (
              <Box sx={{ mt: 2 }}>
                {filteredRequest.length > 0 ? (
                  stableSort(filteredRequest, getComparator(order, orderBy))
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map((row) => (
                      <MobileWorkflowCard
                        key={row.id}
                        row={row}
                        handleProcessForm={handleProcessForm}
                        connectionStatus={connectionStatus}
                        isFormDownloadedForOffline={isFormDownloadedForOffline}
                        getEditedOfflineFormData={getEditedOfflineFormData}
                        hasEditedData={!!getEditedOfflineFormData(row.id)}
                      />
                    ))
                ) : (
                  <Box sx={{ p: 3, textAlign: 'center', color: colors.grayFont }}>
                    {t("lotoWorklow.table.noDownloadedForms")}
                  </Box>
                )}
              </Box>
            )}
          </>
        }
      </PaperContainer>
    </Box>
  );
});
